/* ============================================================================
   numerology-tools.js
   DOM layer for sections/numerology-tool.liquid. Two modes, one file.

   Reads data-nl-tool="wedding" | "compatibility" from the section wrapper.

   SAFETY NOTES
   - Everything is wrapped so a failure degrades to an inline message instead of
     a broken page. This runs on a storefront.
   - User input is NEVER written with innerHTML. Names go in via textContent.
     Putting a visitor's name through innerHTML is a script-injection hole.
   - The maths is NOT here. It lives in numerology-core.js, which must stay in
     step with the paid report's method.
   ============================================================================ */
(function () {
    'use strict';

    var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
    var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    /* One plain handle per number per kind.

       The free page NAMES a number and gives it a handle. It does not interpret
       it. That line is deliberate: interpretation is what the paid reading sells,
       and a free page that never interprets anything cannot show a buyer that the
       reading is any good. So exactly ONE thing gets interpreted in full (see
       insightBlock) and everything else is a name plus a handle. */
    var MEANING = {
        lifePath: {
            1: 'originality and self-direction', 2: 'partnership and sensitivity',
            3: 'expression and connection', 4: 'structure and endurance',
            5: 'change and freedom', 6: 'care and responsibility',
            7: 'study and depth', 8: 'building and material command',
            9: 'completion and service', 11: 'intuition and heightened perception',
            22: 'building at scale', 33: 'care and teaching'
        },
        birthday: {
            1: 'nerve', 2: 'patience', 3: 'charm', 4: 'stamina', 5: 'flexibility',
            6: 'warmth', 7: 'insight', 8: 'judgement', 9: 'perspective',
            11: 'intuition', 22: 'vision', 33: 'teaching'
        },
        expression: {
            1: 'starting things', 2: 'working one to one', 3: 'communicating',
            4: 'organising', 5: 'adapting and improvising',
            6: 'holding things together for other people', 7: 'analysing',
            8: 'running things', 9: 'finishing and giving away',
            11: 'reading people and situations', 22: 'building systems', 33: 'teaching'
        },
        soulUrge: {
            1: 'to lead and set the direction', 2: 'closeness, and to be needed',
            3: 'expression and play', 4: 'stability and something that lasts',
            5: 'freedom and room to move', 6: 'to care for people and have a home',
            7: 'understanding and quiet', 8: 'to build something substantial',
            9: 'to give, and to mean something beyond itself',
            11: 'to be a channel for something larger', 22: 'to build at scale',
            33: 'to teach and care at scale'
        },
        personality: {
            1: 'direct and hard to miss', 2: 'easy, agreeable, low-friction',
            3: 'warm, expressive and quick', 4: 'steady and reliable',
            5: 'restless and hard to pin down', 6: 'warm and responsible',
            7: 'reserved and a little unknowable', 8: 'composed and in charge',
            9: 'open, generous, slightly detached',
            11: 'intuitive and a little electric', 22: 'substantial, quietly authoritative',
            33: 'warm and instructive'
        },
        personalYear: {
            1: 'a beginning', 2: 'a waiting year, things forming',
            3: 'a visible year', 4: 'a work year, foundations',
            5: 'a change year', 6: 'a home year, commitments',
            7: 'a quiet year', 8: 'a harvest year', 9: 'an ending year, clearing out'
        }
    };

    var SIX = [
        ['Life Path', 'lifePath'],
        ['Birthday', 'birthday'],
        ['Expression', 'expression'],
        ['Soul Urge', 'soulUrge'],
        ['Personality', 'personality'],
        ['Personal Year', 'personalYear']
    ];

    function meaning(kind, n) {
        var t = MEANING[kind];
        return (t && t[n]) || ('number ' + n);
    }

    /* ------------------------------------------------------------------ */
    /* THE CONSTELLATION                                                   */
    /*                                                                     */
    /* The signature graphic, and the reason this page has an image at all. */
    /* Six sectors, one per number. Each person sits at a radius set by     */
    /* their OWN value, so an exact match puts the two markers on top of    */
    /* each other and a wide gap pulls them apart. The chord between them   */
    /* is coloured by how that pair actually reads, so the picture and the  */
    /* verdicts can never disagree.                                        */
    /*                                                                     */
    /* It is generated per couple, so no two visitors see the same image,   */
    /* and it costs nothing: pure SVG, no asset, no request.               */
    /*                                                                     */
    /* HONESTY NOTE: the radius is a visual metaphor, not a score. Two      */
    /* numbers can be far apart and still harmonise. That is why the COLOUR */
    /* carries the verdict and the distance only suggests it. Never label   */
    /* this as a measurement.                                              */
    /* ------------------------------------------------------------------ */
    var C_ACCENT = '#fa8c16';
    var C_INK = '#121212';
    var C_RULE = '#efe6da';
    var C_VERDICT = {
        resonates: '#0f4a9c',
        harmonises: '#0a6b37',
        complements: '#b25e00',
        stretches: '#9aa0a8'
    };

    var SVGNS = 'http://www.w3.org/2000/svg';

    function svgNode(tag, attrs) {
        var e = document.createElementNS(SVGNS, tag);
        for (var k in attrs) {
            if (Object.prototype.hasOwnProperty.call(attrs, k)) { e.setAttribute(k, attrs[k]); }
        }
        return e;
    }

    // Closed curve through every point, using midpoints as anchors and the
    // points themselves as control points. Kept for the ring guides.
    function smoothPath(pts) {
        if (pts.length < 3) { return ''; }
        var n = pts.length, i;
        function mid(a, b) { return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]; }
        var start = mid(pts[n - 1], pts[0]);
        var d = 'M' + start[0].toFixed(1) + ',' + start[1].toFixed(1);
        for (i = 0; i < n; i++) {
            var cur = pts[i], m = mid(pts[i], pts[(i + 1) % n]);
            d += 'Q' + cur[0].toFixed(1) + ',' + cur[1].toFixed(1) + ' ' +
                 m[0].toFixed(1) + ',' + m[1].toFixed(1);
        }
        return d + 'Z';
    }

    /* Two rings, one per person, with the actual numbers readable on the dial.

       The first version encoded each number as a radius. It was rejected: values
       jump around (1, 6, 9, 1, 8, 1) so the shape came out a spiky star, and a
       radius still does not tell you WHICH number you are looking at. On a page
       about numbers, the numbers belong in the picture.

       So: the inner ring is one person, the outer ring is the other, each sector
       connects the pair, and the connector is coloured by the verdict. Symmetric,
       always legible, and every couple's dial is different. */
    function constellation(pairs) {
        var W = 280, H = 280, cx = 140, cy = 140;
        var R_OUT = 100, R_IN = 58, PILL = 15;
        var svg = svgNode('svg', {
            viewBox: '0 0 ' + W + ' ' + H,
            'class': 'nl-cst',
            role: 'img',
            'aria-hidden': 'true'
        });

        svg.appendChild(svgNode('circle', { cx: cx, cy: cy, r: R_OUT + PILL + 8, fill: '#fffdfa', stroke: C_RULE, 'stroke-width': '1' }));

        var step = 360 / pairs.length, i, ang, co, si;
        var inner = [], outer = [];

        for (i = 0; i < pairs.length; i++) {
            ang = (-90 + i * step) * Math.PI / 180;
            co = Math.cos(ang); si = Math.sin(ang);
            inner.push([cx + co * R_IN, cy + si * R_IN]);
            outer.push([cx + co * R_OUT, cy + si * R_OUT]);
        }

        svg.appendChild(svgNode('path', {
            d: smoothPath(inner), fill: 'none', stroke: C_RULE, 'stroke-width': '1'
        }));

        // connectors first, so the pills sit on top of them
        for (i = 0; i < pairs.length; i++) {
            var g = svgNode('g', {});
            g.setAttribute('class', 'nl-cst-pair');
            g.style.animationDelay = (0.05 + i * 0.06).toFixed(2) + 's';

            var weak = pairs[i].verdict === 'stretches';
            var chord = svgNode('line', {
                x1: inner[i][0].toFixed(1), y1: inner[i][1].toFixed(1),
                x2: outer[i][0].toFixed(1), y2: outer[i][1].toFixed(1),
                stroke: C_VERDICT[pairs[i].verdict] || C_VERDICT.stretches,
                'stroke-width': weak ? '1.5' : '3',
                'stroke-linecap': 'round',
                opacity: weak ? '.5' : '1'
            });
            chord.setAttribute('class', 'nl-cst-chord');
            chord.style.animationDelay = (0.15 + i * 0.06).toFixed(2) + 's';
            g.appendChild(chord);
            svg.appendChild(g);
        }

        function pill(pt, value, fill, textFill, master) {
            var g = svgNode('g', {});
            if (master) {
                g.appendChild(svgNode('circle', {
                    cx: pt[0].toFixed(1), cy: pt[1].toFixed(1), r: String(PILL + 4),
                    fill: 'none', stroke: fill, 'stroke-width': '1.5', opacity: '.5'
                }));
            }
            g.appendChild(svgNode('circle', {
                cx: pt[0].toFixed(1), cy: pt[1].toFixed(1), r: String(PILL), fill: fill
            }));
            var t = svgNode('text', {
                x: pt[0].toFixed(1), y: pt[1].toFixed(1),
                'text-anchor': 'middle', 'dominant-baseline': 'central',
                fill: textFill, 'font-size': '11', 'font-weight': '600',
                'font-family': 'Poppins, Helvetica, Arial, sans-serif'
            });
            t.textContent = String(value);
            g.appendChild(t);
            return g;
        }

        // Ink text on the accent pill keeps the documented contrast rule
        // (#121212 on #fa8c16 = 7.9:1). White on accent would fail at 2.4:1.
        for (i = 0; i < pairs.length; i++) { svg.appendChild(pill(outer[i], pairs[i].vb, C_INK, '#ffffff', pairs[i].vb >= 11)); }
        for (i = 0; i < pairs.length; i++) { svg.appendChild(pill(inner[i], pairs[i].va, C_ACCENT, C_INK, pairs[i].va >= 11)); }

        return svg;
    }

    function constellationLegend(nameA, nameB) {
        var box = el('div', 'nl-cst__legend');
        [[C_ACCENT, nameA], [C_INK, nameB]].forEach(function (row) {
            var span = el('span');
            var dot = el('i');
            dot.style.background = row[0];
            span.appendChild(dot);
            span.appendChild(document.createTextNode(row[1]));
            box.appendChild(span);
        });
        return box;
    }

    function el(tag, cls, text) {
        var e = document.createElement(tag);
        if (cls) { e.className = cls; }
        if (text !== undefined && text !== null) { e.textContent = String(text); }
        return e;
    }

    function clear(node) {
        while (node.firstChild) { node.removeChild(node.firstChild); }
    }

    function pill(verdict) {
        var map = {
            resonates: 'nl-pill nl-pill--r',
            harmonises: 'nl-pill nl-pill--h',
            complements: 'nl-pill nl-pill--c',
            stretches: 'nl-pill nl-pill--s'
        };
        return el('span', map[verdict] || 'nl-pill nl-pill--s', verdict);
    }

    function parts(iso) {
        var p = String(iso).split('-');
        return { y: Number(p[0]), m: Number(p[1]), d: Number(p[2]) };
    }

    function prettyDate(iso) {
        var p = parts(iso);
        return p.d + ' ' + MONTHS[p.m - 1] + ' ' + p.y;
    }

    function weekday(iso) {
        var p = parts(iso);
        var d = new Date(Date.UTC(p.y, p.m - 1, p.d));
        return DAYS[d.getUTCDay()];
    }

    function datesInMonth(year, month) {
        var out = [];
        var d = new Date(Date.UTC(year, month - 1, 1));
        while (d.getUTCMonth() === month - 1) {
            out.push(d.toISOString().slice(0, 10));
            d.setUTCDate(d.getUTCDate() + 1);
        }
        return out;
    }

    function val(form, name) {
        var f = form.elements[name];
        return f ? String(f.value || '').trim() : '';
    }

    // Name numbers are built from the FULL name. Pythagorean numerology reads every
    // letter, so a surname (and a middle name where one exists) changes the result.
    // Empty parts are skipped so we never produce a double space.
    function fullNameValue(form, suffix) {
        var parts = [val(form, 'name' + suffix), val(form, 'mid' + suffix), val(form, 'last' + suffix)];
        var out = [];
        for (var i = 0; i < parts.length; i++) {
            if (parts[i]) { out.push(parts[i]); }
        }
        return out.join(' ');
    }

    function run() {
        var root = document.querySelector('[data-nl-tool]');
        if (!root) { return; }

        var N = window.Numerology;
        var form = root.querySelector('.nl-form');
        var out = root.querySelector('[data-nl-output]');
        var errBox = root.querySelector('[data-nl-error]');
        var expect = root.querySelector('[data-nl-expect]');
        if (!form || !out) { return; }

        var tool = root.getAttribute('data-nl-tool');
        // Set by the section when cta_link AND cta_label are configured. The
        // report block must not render without a way to act on it: naming what
        // the report adds with no route to it reads as a dead end.
        var withCta = root.getAttribute('data-nl-cta') === '1';

        function showError(msg) {
            if (!errBox) { return; }
            errBox.textContent = msg;
            errBox.hidden = false;
        }

        function hideError() {
            if (!errBox) { return; }
            errBox.hidden = true;
            errBox.textContent = '';
        }

        if (!N) {
            showError('The calculator could not load. Please refresh the page.');
            return;
        }

        /* ---------------- shared bits ---------------- */
        function seal(num) {
            var s = el('div', 'nl-seal');
            s.appendChild(el('b', null, num));
            return s;
        }

        function pillsRow(pairs) {
            var row = el('div', 'nl-pills');
            for (var i = 0; i < pairs.length; i++) {
                var chip = el('span', 'nl-pill-row');
                chip.appendChild(el('span', 'nl-pill-key', pairs[i][0]));
                chip.appendChild(pill(pairs[i][1]));
                row.appendChild(chip);
            }
            return row;
        }

        // Every part of a name must be readable ON ITS OWN. A total-only check is
        // not enough: a Devanagari first name followed by a Latin surname still
        // totals four usable letters, so it passes and the engine quietly computes
        // the SURNAME's number while the page presents it as the full-name number.
        // Same silent-wrong-answer class as rendering Expression 0.
        var PART_LABEL = { A: 'first', B: 'second' };
        function assertNameReadable(suffix) {
            var parts = [val(form, 'name' + suffix), val(form, 'mid' + suffix), val(form, 'last' + suffix)];
            for (var i = 0; i < parts.length; i++) {
                if (parts[i] && N.letterCount(parts[i]) === 0) {
                    throw new Error('Please spell the ' + PART_LABEL[suffix] + ' person\'s name in Latin letters, for example "Ria Sharma". Every part of a name is read, so each one has to be readable.');
                }
            }
        }

        /* ---------------- wedding ---------------- */
        function renderWedding() {
            var dobA = val(form, 'dobA');
            var dobB = val(form, 'dobB');
            var rawA = fullNameValue(form, 'A');
            var rawB = fullNameValue(form, 'B');
            var month = parseInt(val(form, 'month'), 10);
            var year = parseInt(val(form, 'year'), 10);

            if (!dobA || !dobB) { throw new Error('Please enter both dates of birth.'); }
            if (!month || !year) { throw new Error('Please choose a month and a year.'); }

            // Names stay OPTIONAL here: the ranking runs entirely on the dates and
            // must work without them. When they ARE given the name numbers appear;
            // when they are not, the page says what is missing instead of quietly
            // showing three numbers and implying that is the whole picture.
            var withNames = N.isReadableName(rawA) && N.isReadableName(rawB);
            // Placeholders follow the FORM labels, where A is "their first name"
            // and B is "your first name". Generic A/B would be ambiguous here.
            var nameA = rawA || 'Your partner';
            var nameB = rawB || 'You';

            var all = datesInMonth(year, month);
            var ranked = N.rankDates(all, dobA, dobB);
            var top = ranked.slice(0, 3);
            var best = ranked[0];
            var monthLabel = MONTHS[month - 1] + ' ' + year;

            var profA = N.profile(dobA, withNames ? nameA : null, year);
            var profB = N.profile(dobB, withNames ? nameB : null, year);

            var pairs = buildPairs(profA, profB);

            // A - the ANSWER and the constellation together. The picture is the
            // peak of the page, so it belongs at the top rather than buried under
            // three date cards.
            out.appendChild(heroCard(
                weekday(best.date) + ' ' + prettyDate(best.date),
                'Your strongest date in ' + monthLabel + ', scored across all ' + all.length + ' days of the month.',
                'Each ring is one of you. Where the two markers meet, that number agrees.',
                pairs, nameA, nameB));

            // B - the three dates
            for (var i = 0; i < top.length; i++) {
                var r = top[i];
                var card = el('div', 'nl-date');
                card.appendChild(seal(r.universalDay));

                var body = el('div', 'nl-date-body');
                var head = el('div', 'nl-date-head');
                head.appendChild(el('span', 'nl-date-val', prettyDate(r.date)));
                head.appendChild(el('span', 'nl-rank', r.label));
                body.appendChild(head);
                body.appendChild(el('div', 'nl-date-label', weekday(r.date)));
                body.appendChild(el('p', 'nl-date-char',
                    'Universal Day ' + r.universalDay + ' (' + r.character + '): ' + r.characterNote + '.'));

                var pairs = [];
                for (var k = 0; k < r.components.length; k++) {
                    pairs.push([r.components[k].label, r.components[k].verdict]);
                }
                body.appendChild(pillsRow(pairs));
                card.appendChild(body);
                out.appendChild(card);
            }

            // C - the numbers the ranking was actually built from
            out.appendChild(el('h2', 'nl-h2', 'Your numbers for ' + year));
            out.appendChild(el('p', 'nl-sub', withNames
                ? 'Read from both full names and both dates of birth.'
                : 'Add both names above to see your Expression, Soul Urge and Personality as well.'));

            out.appendChild(numbersGrid([[nameA, profA], [nameB, profB]], pairs));

            // D - why this date came first
            out.appendChild(weddingInsight(best, ranked, monthLabel, nameA, nameB, withNames));

            // E - where the year sits for each of them
            out.appendChild(weddingTiming(nameA, profA, nameB, profB, year, !rawB));

            if (withCta) { out.appendChild(reportBlock('wedding')); }
        }

        // Block D for the wedding tool. Explains the ranking rather than adding
        // ceremony: which component won it, and how much of the month was close.
        function weddingInsight(best, ranked, monthLabel, nameA, nameB, withNames) {
            var box = el('div', 'nl-insight');
            box.appendChild(el('h3', null, 'Why this date came first'));

            var exact = null, near = null, k, c;
            for (k = 0; k < best.components.length; k++) {
                c = best.components[k];
                if (c.verdict === 'resonates' && !exact) { exact = c; }
                if (c.verdict === 'harmonises' && !near) { near = c; }
            }
            var exactDays = 0;
            for (k = 0; k < ranked.length; k++) { if (ranked[k].resonates > 0) { exactDays++; } }

            if (exact) {
                box.appendChild(para(['The day number for ', { b: prettyDate(best.date) }, ' is ',
                    { b: String(best.universalDay) }, ', which is the same as your ', exact.label,
                    '. In this method an exact match outranks everything else, which is what puts it above the dates nearest to it.']));
            } else if (near) {
                box.appendChild(para(['The day number for ', { b: prettyDate(best.date) }, ' is ',
                    { b: String(best.universalDay) }, ', which shares a group with your ', near.label,
                    '. There is no exact match anywhere in this ranking, so it wins on the weight of several partial agreements rather than on one clear signal.']));
            } else {
                box.appendChild(para(['No day in ', { b: monthLabel }, ' matches one of your numbers exactly, so this ranking is by degree rather than by a clear winner. ',
                    { b: prettyDate(best.date) }, ' is simply the day that asks the least of you.']));
            }

            box.appendChild(para(['Across the month, ', { b: String(exactDays) }, ' of the ', { b: String(ranked.length) },
                ' days carry an exact match to one of your numbers. That is the spread you are actually choosing from, not only the three shown above.']));

            if (withNames) {
                var exA = N.expression(nameA), exB = N.expression(nameB);
                var vv = N.harmony(exA, exB);
                box.appendChild(para(['Separately from the calendar, your names give an Expression pair of ', { b: String(exA) },
                    ' and ', { b: String(exB) }, ', which ', verdictVerb(vv),
                    '. That is about the two of you rather than about the day.']));
            }
            return box;
        }

        // Block E for the wedding tool. The year matters as much as the day, and a
        // Personal Year runs on the calendar, so the reading genuinely expires.
        function weddingTiming(nameA, A, nameB, B, year, bIsYou) {
            var box = el('div', 'nl-timing');
            box.appendChild(el('h3', null, 'Where ' + year + ' sits for each of you'));
            var grid = el('div', 'nl-timing-grid');
            [[nameA, A.personalYear], [nameB, B.personalYear]].forEach(function (row) {
                var cell = el('div', 'nl-timing-cell');
                cell.appendChild(el('span', null, row[0]));
                cell.appendChild(el('b', null, row[1]));
                cell.appendChild(el('em', null, meaning('personalYear', row[1])));
                grid.appendChild(cell);
            });
            box.appendChild(grid);
            if (A.personalYear === B.personalYear) {
                box.appendChild(para(['You are both in a ', { b: String(A.personalYear) }, ' year in ', { b: String(year) },
                    ', so you are working on the same kind of thing in the year you are planning around.']));
            } else {
                // "You" is a pronoun and cannot take "is", so the second clause
                // is built differently when the visitor left the name field blank.
                var segs = ['You are on different clocks in ', { b: String(year) }, ': ',
                    { b: nameA }, ' is in a ', { b: String(A.personalYear) }, ' year and '];
                if (bIsYou) {
                    segs.push('you are in a ', { b: String(B.personalYear) }, ' year');
                } else {
                    segs.push({ b: nameB }, ' is in a ', { b: String(B.personalYear) }, ' year');
                }
                segs.push('. A wedding does not need the two of you in the same season, but it helps to know that you are not.');
                box.appendChild(para(segs));
            }
            box.appendChild(para(['A Personal Year runs on the calendar rather than on your birthday, so the same month reads differently in a different year. Which day you choose and which year you choose are two separate decisions.']));
            return box;
        }

        /* ---------------- results ladder ---------------- */

        // Paragraph builder. Strings become text, {b:'...'} becomes bold. Every
        // segment goes in as a text node, so a visitor's name can never be parsed
        // as markup.
        function para(segs) {
            var p = el('p');
            for (var i = 0; i < segs.length; i++) {
                var s = segs[i];
                if (typeof s === 'string') { p.appendChild(document.createTextNode(s)); }
                else { p.appendChild(el('b', null, s.b)); }
            }
            return p;
        }

        function gapKind(su, pe) {
            if (su === pe) { return 'aligned'; }
            var h = N.harmony(su, pe);
            if (h === 'resonates' || h === 'harmonises') { return 'near'; }
            return 'wide';
        }

        // One row per number the two people actually have. Wedding skips the
        // name numbers when no names were entered, so the list is built from the
        // profiles rather than assuming all six exist.
        function buildPairs(A, B) {
            var list = [];
            for (var i = 0; i < SIX.length; i++) {
                var key = SIX[i][1];
                if (A[key] === undefined || B[key] === undefined) { continue; }
                list.push({ label: SIX[i][0], va: A[key], vb: B[key], verdict: N.harmony(A[key], B[key]) });
            }
            return list;
        }

        // The six numbers as two cards. Life Path leads: it spans the full row
        // and carries a much larger figure, because twelve identical tiles gave
        // the eye nothing to land on. Each tile also carries its pair's verdict
        // as a colour bar, so the grid and the alignment list cannot disagree.
        function numbersGrid(people, pairs) {
            var verdictFor = {};
            for (var q = 0; q < pairs.length; q++) { verdictFor[pairs[q].label] = pairs[q].verdict; }

            var wrap = el('div', 'nl-pair');
            for (var p = 0; p < people.length; p++) {
                var card = el('div', 'nl-person');
                card.appendChild(el('h3', null, people[p][0]));
                var prof = people[p][1];
                var nums = el('div', 'nl-nums');
                for (var i = 0; i < SIX.length; i++) {
                    var key = SIX[i][1], label = SIX[i][0];
                    if (prof[key] === undefined) { continue; }
                    var cls = 'nl-num';
                    if (i === 0) { cls += ' nl-num--lead'; }
                    if (prof[key] >= 11) { cls += ' nl-num--master'; }
                    var cell = el('span', cls);
                    cell.appendChild(document.createTextNode(label));
                    cell.appendChild(el('b', null, prof[key]));
                    cell.appendChild(el('small', null, meaning(key, prof[key])));
                    var v = verdictFor[label];
                    if (v) {
                        var bar = el('i', 'nl-num__bar');
                        bar.style.background = C_VERDICT[v] || C_VERDICT.stretches;
                        cell.appendChild(bar);
                    }
                    nums.appendChild(cell);
                }
                card.appendChild(nums);
                wrap.appendChild(card);
            }
            return wrap;
        }

        // The same six pairs written out. Colour is redundant on purpose: the
        // verdict word always appears, so nothing depends on seeing a hue.
        function alignmentList(pairs) {
            var wrap = el('div', 'nl-align');
            for (var i = 0; i < pairs.length; i++) {
                var p = pairs[i];
                var row = el('div', 'nl-align__row');
                var dot = el('i', 'nl-align__dot');
                dot.style.background = C_VERDICT[p.verdict] || C_VERDICT.stretches;
                row.appendChild(dot);
                row.appendChild(el('span', 'nl-align__key', p.label + ' ' + p.va + ' x ' + p.vb));
                row.appendChild(el('span', 'nl-align__val', p.verdict));
                wrap.appendChild(row);
            }
            return wrap;
        }

        function wrapText(g, text, x, y, maxW, lineH) {
            var words = String(text).split(' '), line = '', lines = [], i, test;
            for (i = 0; i < words.length; i++) {
                test = line ? line + ' ' + words[i] : words[i];
                if (g.measureText(test).width > maxW && line) { lines.push(line); line = words[i]; }
                else { line = test; }
            }
            if (line) { lines.push(line); }
            for (i = 0; i < lines.length; i++) { g.fillText(lines[i], x, y + i * lineH); }
            return y + lines.length * lineH;
        }

        /* Share card.

           Pinterest is the planned traffic source and this dial is the only
           thing on the site worth pinning, so the export is built at 1080x1350
           (4:5, the tall pin shape) rather than at whatever size the page happens
           to be. Drawn with canvas rather than rasterising the SVG because an SVG
           loaded as an image cannot use the page's webfont, which would silently
           export the numbers in a fallback face. */
        function saveCard(pairs, nameA, nameB, title, btn) {
            var W = 1080, H = 1350;
            var cv = document.createElement('canvas');
            cv.width = W; cv.height = H;
            var g = cv.getContext('2d');
            if (!g) { return; }
            var label = btn.textContent;
            btn.disabled = true;
            btn.textContent = 'Preparing image...';

            function draw() {
                var grd = g.createLinearGradient(0, 0, W, H);
                grd.addColorStop(0, '#fffdf9');
                grd.addColorStop(1, '#fff2e2');
                g.fillStyle = grd;
                g.fillRect(0, 0, W, H);
                g.textAlign = 'center';

                g.fillStyle = '#b25e00';
                g.font = '700 20px Poppins, Helvetica, Arial, sans-serif';
                g.fillText('E N L I G H T N R', W / 2, 104);

                g.fillStyle = '#121212';
                g.font = '600 58px Poppins, Helvetica, Arial, sans-serif';
                wrapText(g, title, W / 2, 214, W - 200, 66);

                var cx = W / 2, cy = 690, R_OUT = 196, R_IN = 112, PILL = 32;
                var step = 360 / pairs.length, i, ang, a, p1, p2, weak;

                function pt(r, deg) {
                    a = deg * Math.PI / 180;
                    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
                }

                g.strokeStyle = '#efe6da'; g.lineWidth = 2;
                g.beginPath(); g.arc(cx, cy, R_OUT + PILL + 26, 0, Math.PI * 2); g.stroke();

                for (i = 0; i < pairs.length; i++) {
                    ang = -90 + i * step;
                    p1 = pt(R_IN, ang); p2 = pt(R_OUT, ang);
                    weak = pairs[i].verdict === 'stretches';
                    g.strokeStyle = C_VERDICT[pairs[i].verdict] || C_VERDICT.stretches;
                    g.lineWidth = weak ? 3 : 8;
                    g.globalAlpha = weak ? 0.5 : 1;
                    g.beginPath(); g.moveTo(p1[0], p1[1]); g.lineTo(p2[0], p2[1]); g.stroke();
                    g.globalAlpha = 1;
                }

                function pill(p, v, fill, ink) {
                    g.fillStyle = fill;
                    g.beginPath(); g.arc(p[0], p[1], PILL, 0, Math.PI * 2); g.fill();
                    g.fillStyle = ink;
                    g.font = '600 23px Poppins, Helvetica, Arial, sans-serif';
                    g.fillText(String(v), p[0], p[1] + 8);
                }
                for (i = 0; i < pairs.length; i++) { pill(pt(R_OUT, -90 + i * step), pairs[i].vb, '#121212', '#ffffff'); }
                for (i = 0; i < pairs.length; i++) { pill(pt(R_IN, -90 + i * step), pairs[i].va, '#fa8c16', '#121212'); }

                g.font = '500 25px Poppins, Helvetica, Arial, sans-serif';
                var y = 1128;
                g.fillStyle = '#fa8c16';
                g.beginPath(); g.arc(W / 2 - 190, y - 8, 8, 0, Math.PI * 2); g.fill();
                g.fillStyle = '#4a5058';
                g.textAlign = 'left'; g.fillText(nameA, W / 2 - 172, y);
                g.fillStyle = '#121212';
                g.beginPath(); g.arc(W / 2 + 24, y - 8, 8, 0, Math.PI * 2); g.fill();
                g.fillStyle = '#4a5058'; g.fillText(nameB, W / 2 + 42, y);

                g.textAlign = 'center';
                g.fillStyle = '#8a8f98';
                g.font = '500 21px Poppins, Helvetica, Arial, sans-serif';
                g.fillText('enlightnr.com', W / 2, 1244);

                cv.toBlob(function (blob) {
                    if (blob) {
                        var link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = 'enlightnr-' +
                            String(title).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '.png';
                        document.body.appendChild(link);
                        link.click();
                        window.setTimeout(function () { URL.revokeObjectURL(link.href); link.remove(); }, 1500);
                    }
                    btn.disabled = false;
                    btn.textContent = label;
                }, 'image/png');
            }

            // Wait for the webfont, or the numbers export in a fallback face.
            if (document.fonts && document.fonts.ready) { document.fonts.ready.then(draw); }
            else { draw(); }
        }

        function heroCard(title, sub, hint, pairs, nameA, nameB) {
            var card = el('div', 'nl-dynamic nl-dynamic--chart');
            var text = el('div', 'nl-dynamic__text');
            text.appendChild(el('h2', null, title));
            if (sub) { text.appendChild(el('p', null, sub)); }
            if (hint) { text.appendChild(el('p', 'nl-dynamic__hint', hint)); }
            card.appendChild(text);
            var chart = el('div', 'nl-dynamic__chart');
            chart.appendChild(constellation(pairs));
            chart.appendChild(constellationLegend(nameA, nameB));
            var save = el('button', 'nl-save', 'Save this reading');
            save.type = 'button';
            save.addEventListener('click', function () { saveCard(pairs, nameA, nameB, title, save); });
            chart.appendChild(save);
            card.appendChild(chart);
            return card;
        }

        // Turn a verdict token into something that reads as a clause.
        function verdictVerb(v) {
            if (v === 'resonates') { return 'match exactly'; }
            if (v === 'harmonises') { return 'sit in the same group'; }
            if (v === 'complements') { return 'meet through a bridge'; }
            return 'do not share a language';
        }

        // Block C. The SINGLE complete insight, and the only place the free page
        // interprets anything. Withholding every interpretation made the results
        // read as a lookup and gave the buyer no evidence the paid reading is any
        // good. This demonstrates the quality, then stops on purpose.
        function insightBlock(nA, A, nB, B) {
            var box = el('div', 'nl-insight');
            box.appendChild(el('h3', null, 'The one thing worth knowing first'));

            var ga = gapKind(A.soulUrge, A.personality);
            var gb = gapKind(B.soulUrge, B.personality);
            var wides = (ga === 'wide' ? 1 : 0) + (gb === 'wide' ? 1 : 0);

            var suA = meaning('soulUrge', A.soulUrge), peA = meaning('personality', A.personality);
            var suB = meaning('soulUrge', B.soulUrge), peB = meaning('personality', B.personality);

            if (wides === 2) {
                box.appendChild(para([{ b: nA }, ' wants ', suA, ' and is read as ', peA,
                    '. ', { b: nB }, ' wants ', suB, ' and is read as ', peB,
                    '. Neither of those two descriptions matches itself.']));
                box.appendChild(para(['That mismatch is the most useful thing on this page. When what someone wants and how they come across are the same number, they are easy to read and rarely misunderstood. When they differ, people respond to the outward one and miss the inward one, and the person left doing the correcting is the one being misread.']));
                box.appendChild(para(['Two people with that gap in the same household is a specific situation. You each keep explaining yourselves, neither explanation lands cleanly, and you are both fluent in being read wrongly. It also means something genuinely useful, which neither of you probably says out loud.']));
            } else if (wides === 1) {
                var isA = ga === 'wide';
                var wName = isA ? nA : nB, wWant = isA ? suA : suB, wRead = isA ? peA : peB;
                var fName = isA ? nB : nA, fWant = isA ? suB : suA, fRead = isA ? peB : peA;
                box.appendChild(para([{ b: wName }, ' wants ', wWant, ' and is read as ', wRead,
                    ', which are not the same thing. ', { b: fName }, ' wants ', fWant,
                    ' and is read as ', fRead, ', which are.']));
                box.appendChild(para(['The asymmetry matters more than either number. The one whose inside and outside agree is taken at face value and gets the benefit of being predictable. The other spends energy being interpreted, and from outside that can look like inconsistency when it is really consistency with something the other person cannot see yet.']));
                box.appendChild(para(['It explains a particular kind of disagreement, the one where both people are being entirely sincere and still talking past each other.']));
            } else {
                box.appendChild(para([{ b: nA }, ' wants ', suA, ' and is read as ', peA,
                    '. ', { b: nB }, ' wants ', suB, ' and is read as ', peB,
                    '. In both cases the inside and the outside agree.']));
                box.appendChild(para(['That is uncommon, and it buys you something specific: neither of you is spending energy correcting an impression. What it does not buy you is practice at it. People who are read accurately tend to assume accuracy is normal, and the first time one of you is genuinely misread, neither of you will have a routine for it.']));
                box.appendChild(para(['It is also why the two of you can be hard for other people to read as a pair while being easy for each other.']));
            }
            return box;
        }

        // Block D. Names the loosest pair and deliberately does not resolve it.
        // When nothing is genuinely loose it says so rather than inventing tension.
        function tensionBlock(nA, A, nB, B) {
            var w = null, i, key, v, pts;
            for (i = 0; i < SIX.length; i++) {
                key = SIX[i][1];
                v = N.harmony(A[key], B[key]);
                pts = N.harmonyPoints(v);
                if (!w || pts < w.pts) {
                    w = { label: SIX[i][0], a: A[key], b: B[key], verdict: v, pts: pts };
                }
            }
            var box = el('div', 'nl-tension');
            box.appendChild(el('h3', null, 'Where it takes effort'));
            if (w.pts === 0) {
                box.appendChild(para(['Your ', w.label.toLowerCase(), ' numbers, ', { b: String(w.a) },
                    ' and ', { b: String(w.b) }, ', do not share a language. That does not make them opposites and it does not make them incompatible. They do not read each other by default, and the effort shows up as a difference of pace rather than of intent.']));
            } else if (w.pts === 1) {
                box.appendChild(para(['Your ', w.label.toLowerCase(), ' numbers, ', { b: String(w.a) },
                    ' and ', { b: String(w.b) }, ', meet through a bridge rather than a shared group. That is workable, and often useful, but it is not automatic: you get to the same place by different routes, and neither route looks obviously correct to the other.']));
            } else {
                box.appendChild(para(['Nothing here pulls hard against you. The loosest of your six pairs is ', w.label.toLowerCase(), ' ', { b: String(w.a) }, ' against ', { b: String(w.b) }, ', which is a difference of emphasis rather than a fault line. The honest reading is that the friction in this pairing is not in the numbers.']));
            }
            return box;
        }

        // Block E. The only honest source of urgency here: a Personal Year runs on
        // the calendar, so the reading genuinely expires.
        function timingBlock(nA, A, nB, B, year) {
            var box = el('div', 'nl-timing');
            box.appendChild(el('h3', null, 'Where you are in ' + year));
            var grid = el('div', 'nl-timing-grid');
            [[nA, A.personalYear], [nB, B.personalYear]].forEach(function (row) {
                var cell = el('div', 'nl-timing-cell');
                cell.appendChild(el('span', null, row[0]));
                cell.appendChild(el('b', null, row[1]));
                cell.appendChild(el('em', null, meaning('personalYear', row[1])));
                grid.appendChild(cell);
            });
            box.appendChild(grid);
            if (A.personalYear === B.personalYear) {
                box.appendChild(para(['You are both in a ', { b: String(A.personalYear) },
                    ' year. Being on the same clock is a real advantage: you are solving the same kind of problem at the same time, so decisions tend to be easier to land together in a year like this one.']));
            } else {
                box.appendChild(para(['You are on different clocks. ', { b: String(A.personalYear) },
                    ' is ', meaning('personalYear', A.personalYear), ', and ', { b: String(B.personalYear) },
                    ' is ', meaning('personalYear', B.personalYear), '. Two different seasons can still work, but it means you are not optimising for the same thing this year, which is worth knowing before you plan around each other.']));
            }
            box.appendChild(para(['A Personal Year runs on the calendar rather than on your birthday, so it changes every January. The same two people read differently a year from now, which is why a reading like this is tied to a date.']));
            return box;
        }

        // Block F. Only rendered when the section has a CTA configured. Naming what
        // the report adds with no route to it reads as a dead end, and the two
        // products promise different things, so the list is per-tool.
        var REPORT_ITEMS = {
            compatibility: {
                lead: 'This page names all six of your numbers and interprets one thing in full. The written reading picks up exactly where that stops.',
                items: [
                    'What each of your six number pairs means, not only how the two numbers sit together',
                    'The behaviour each of you defaults to under pressure, and what sets it off',
                    'What to do about the pair that takes effort, specifically rather than in general',
                    'Your two cycles in detail, and what the next two years look like for each of you',
                    'A written reading for both of you, prepared by hand from these numbers'
                ]
            },
            wedding: {
                lead: 'This page ranks one month and explains the date that came first. The written reading picks up exactly where that stops.',
                items: [
                    'Every day of the months you are considering, ranked and explained',
                    'How each candidate date sits against each of your six numbers',
                    'The dates you are already considering, assessed one at a time',
                    'Which year suits a wedding better, given both of your cycles',
                    'A written reading for both of you, prepared by hand from these numbers'
                ]
            }
        };

        function reportBlock(kind) {
            var cfg = REPORT_ITEMS[kind] || REPORT_ITEMS.compatibility;
            var box = el('div', 'nl-report');
            box.appendChild(el('h3', null, 'What the full reading adds'));
            box.appendChild(para([cfg.lead]));
            var ul = el('ul');
            for (var i = 0; i < cfg.items.length; i++) {
                var li = el('li', null, cfg.items[i]);
                if (i >= 3) { li.className = 'is-locked'; }
                ul.appendChild(li);
            }
            box.appendChild(ul);
            return box;
        }

        /* ---------------- compatibility ---------------- */
        function renderCompatibility() {
            var firstA = val(form, 'nameA');
            var firstB = val(form, 'nameB');
            var dobA = val(form, 'dobA');
            var dobB = val(form, 'dobB');
            var nameA = fullNameValue(form, 'A');
            var nameB = fullNameValue(form, 'B');

            if (!firstA || !firstB || !val(form, 'lastA') || !val(form, 'lastB')) {
                throw new Error('Please enter a first name and a last name for both people.');
            }
            if (!dobA || !dobB) { throw new Error('Please enter both dates of birth.'); }

            assertNameReadable('A', 'the first');
            assertNameReadable('B', 'the second');
            if (!N.isReadableName(nameA) || !N.isReadableName(nameB)) {
                throw new Error('Please spell both names in Latin letters, for example "Ria Sharma".');
            }

            var year = new Date().getFullYear();
            var A = N.profile(dobA, nameA, year);
            var B = N.profile(dobB, nameB, year);
            var c = N.compatibility(dobA, nameA, dobB, nameB);

            var pairs = buildPairs(A, B);

            // A - the constellation. The peak of the page, and the only part
            // that looks different for every visitor.
            out.appendChild(heroCard(
                c.dynamic,
                nameA + ' and ' + nameB,
                'Each ring is one of you. Where the two markers meet, that number agrees. The line between them is coloured by how the pair reads.',
                pairs, nameA, nameB));

            // B - all six numbers per person, each carrying its own verdict
            out.appendChild(numbersGrid([[nameA, A], [nameB, B]], pairs));

            // C - the same six pairs, written out
            out.appendChild(alignmentList(pairs));

            // C, D, E, then F only when there is somewhere to send the visitor
            out.appendChild(insightBlock(nameA, A, nameB, B));
            out.appendChild(tensionBlock(nameA, A, nameB, B));
            out.appendChild(timingBlock(nameA, A, nameB, B, year));
            if (withCta) { out.appendChild(reportBlock('compatibility')); }
        }

        // Hand the entered details to the order page so the buyer does not retype
        // them. sessionStorage only: the values stay in the visitor's own browser
        // and never travel in a URL, so they cannot land in server logs, referrers
        // or analytics. Wrapped because private mode can throw on access.
        function stashPrefill() {
            try {
                var payload = {
                    tool: tool,
                    nameA: val(form, 'nameA'), midA: val(form, 'midA'),
                    lastA: val(form, 'lastA'), dobA: val(form, 'dobA'),
                    nameB: val(form, 'nameB'), midB: val(form, 'midB'),
                    lastB: val(form, 'lastB'), dobB: val(form, 'dobB'),
                    // Only the wedding tool has these. Carried so the report can be
                    // prepared for the months the couple actually asked about.
                    month: val(form, 'month'), year: val(form, 'year')
                };
                window.sessionStorage.setItem('nl_prefill', JSON.stringify(payload));
            } catch (e) { /* storage unavailable - prefill is a convenience, not a requirement */ }
        }

        form.addEventListener('submit', function (ev) {
            ev.preventDefault();
            if (form.getAttribute('data-busy') === '1') { return; }
            form.setAttribute('data-busy', '1');
            hideError();

            // A short, deliberate beat before the reveal. The results are the
            // peak of this page (peak-end rule), and dumping twelve numbers the
            // instant a button is clicked spends that moment for nothing. Kept
            // under 400ms on purpose: waiting should feel like effort being made,
            // not like a spinner.
            var btn = form.querySelector('.nl-btn');
            var label = btn ? btn.textContent : '';
            if (btn) { btn.disabled = true; btn.textContent = 'Reading your numbers...'; }

            window.setTimeout(function () {
                clear(out);
                try {
                    if (tool === 'wedding') { renderWedding(); }
                    else { renderCompatibility(); }
                    out.hidden = false;
                    if (expect) { expect.hidden = true; }
                    stashPrefill();
                    if (out.scrollIntoView) {
                        out.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                } catch (e) {
                    out.hidden = true;
                    showError(e && e.message ? e.message
                        : 'Something went wrong reading those dates. Please check them and try again.');
                }
                if (btn) { btn.disabled = false; btn.textContent = label; }
                form.removeAttribute('data-busy');
            }, 380);
        });
    }

    try {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', run);
        } else {
            run();
        }
    } catch (e) {
        /* Never let this file break the storefront. */
    }
}());
